import { Component, OnInit } from '@angular/core';
import { DisplayOptions } from '../../../shared/interfaces/display-options';
import {
    PerformanceScorecardService,
    ScorecardProductionWebModel,
    EntityHierarchy,
    IProductionPurposeMixPieChartData,
    IScorecardParameters,
    ScorecardType,
} from '../../services/performance-scorecard.service';
import { ReusableFunctions } from '../../../shared/classes/reusable-functions';
import {
    NoDataActionType,
    NoDataSourceType,
    NoDataTypes,
} from '../../../shared/interfaces/no-data';
import { ActivatedRoute, Router } from '@angular/router';
import {
    ScorecardKpicardDataHandler,
    IScorecardKpiParams,
} from '../../../shared/classes/scorecards-kpi-card-data-handler';
import { ICardlistData } from '../../../shared/components/card-list/card-list.component';
import { DomSanitizer } from '@angular/platform-browser';
import { ILargeCardInteriorOptions } from '../../../shared/interfaces/card-data';
import { IScorecardDetailsCardData } from '../../../shared/components/scorecards-score-summary-card/scorecards-score-summary-card.component';

@Component({
    selector: 'app-performance-scorecard-entity',
    templateUrl: './performance-scorecard-entity.component.html',
    styleUrls: ['./performance-scorecard-entity.component.scss'],
})
export class PerformanceScorecardEntityComponent implements OnInit {
    hierarchyLevelsOfInterest: EntityHierarchy[] = [];
    entitiesOfInterest: EntityHierarchy[] = [];
    selectedIdentifier: EntityHierarchy[] = [];
    selectedLevel: EntityHierarchy[] = [];
    entityListSettings: any;
    levelListSettings: any;
    pageTitle = 'Branch Scorecard';
    displayOptions: DisplayOptions[];
    branchIdentifier: string;
    fromDatePeriod: string = 'This Year';
    ymc: string;
    roleName: string;
    idFromQueryParams: string;
    kpiCardData: ICardlistData;
    action: NoDataActionType;
    noDataSelection: NoDataTypes;
    productionPurposeMixPieChartData: IProductionPurposeMixPieChartData;
    pageId = 'performanceScorecardEntityType';
    largeCardInteriorOptions: ILargeCardInteriorOptions;
    detailsCard: IScorecardDetailsCardData;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private performanceScorecardService: PerformanceScorecardService,
        private reusableFunctions: ReusableFunctions,
        private sanitizer: DomSanitizer
    ) {}

    ngOnInit() {
        this.getQueryParams();
        this.getRoles();

        this.entityListSettings = {
            singleSelection: true,
            text: 'Select Entity',
            enableSearchFilter: true,
            showCheckbox: false,
            labelKey: 'hierarchyName',
            primaryKey: 'hierarchyIdentifier',
            enableFilterSelectAll: false,
        };

        this.levelListSettings = {
            singleSelection: true,
            text: 'Select Level',
            enableSearchFilter: false,
            showCheckbox: false,
            labelKey: 'roleName',
            primaryKey: 'roleLevel',
            enableFilterSelectAll: false,
        };

        this.noDataSelection = {
            action: NoDataActionType.emptySelect,
            searchTerm: '',
            componentTitle: 'entity',
            inputSource: NoDataSourceType.largeDataCard,
            wrapInACard: true,
        };

        this.productionPurposeMixPieChartData = this.performanceScorecardService.initializeDonutcharts();
        this.largeCardInteriorOptions = this.performanceScorecardService.initializeProductionChart(
            this.pageId,
            this.action
        );

        this.initializeDetailCard();

        this.displayOptions = this.reusableFunctions.getCurrentDisplayOptions(
            '/performance/scorecards/branch'
        );
        this.setDisplayOptionsSelected();
    }

    private getRoles() {
        this.performanceScorecardService.getEntityRolesByUser().subscribe(
            (res) => {
                if (res && res.length) {
                    this.hierarchyLevelsOfInterest = res;

                    if (this.roleName && this.roleName.length) {
                        let recordInList = this.hierarchyLevelsOfInterest.find(
                            (x) => x.roleName.toLowerCase() === this.roleName.toLowerCase()
                        );
                        if (recordInList) {
                            this.onLevelChange(this.roleName);
                        }
                    } else {
                        if (
                            this.hierarchyLevelsOfInterest &&
                            this.hierarchyLevelsOfInterest.length == 1
                        ) {
                            this.onLevelChange(
                                this.hierarchyLevelsOfInterest[0].hierarchyIdentifier
                            );
                        }
                    }
                }
            },
            (error) => {
                this.noDataSelection.action = NoDataActionType.error;
                throw new Error(error);
            }
        );
    }

    private getRoleHierarchies(roleName: string) {
        this.performanceScorecardService.getEntityHierarchiesByRole(roleName).subscribe(
            (res) => {
                if (res && res.length) {
                    this.entitiesOfInterest = res;

                    if (this.idFromQueryParams && this.idFromQueryParams.length) {
                        let recordFromList = this.entitiesOfInterest.find(
                            (x) => x.hierarchyIdentifier === this.idFromQueryParams
                        );

                        if (recordFromList) {
                            this.onEntityChange(recordFromList);
                        }
                    } else {
                        if (this.entitiesOfInterest && this.entitiesOfInterest.length == 1) {
                            this.onEntityChange(this.entitiesOfInterest[0]);
                        }
                    }
                }
            },
            (error) => {
                this.noDataSelection.action = NoDataActionType.error;
                throw new Error(error);
            }
        );
    }

    private getQueryParams() {
        this.route.queryParamMap.subscribe((p) => {
            if (p) {
                if (p.get('level')) {
                    this.roleName = p.get('level');
                    if (p.get('id')) {
                        this.idFromQueryParams = p.get('id');
                    }
                }
            }
        });
    }

    updateQueryParams() {
        this.clearIdFromQueryParams();
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                level: this.roleName ? this.roleName : this.selectedLevel[0].roleName,
                id: this.selectedIdentifier[0].hierarchyIdentifier,
            },
            queryParamsHandling: 'merge',
        });
    }

    applyDisplayOptions(event: DisplayOptions[]) {
        this.action = NoDataActionType.filter;
        this.displayOptions = event;
        this.setDisplayOptionsSelected();
        this.getScorecardData();
    }

    onLevelChange(roleName: string) {
        if (this.selectedLevel && this.selectedLevel.length) {
            this.clearIdFromQueryParams();
        }

        this.selectedLevel = [
            this.hierarchyLevelsOfInterest.find(
                (level) => level.roleName.toString() === roleName.toString()
            ),
        ];

        if (this.selectedIdentifier && this.selectedIdentifier.length) {
            this.selectedIdentifier = [];
        }

        this.roleName = this.selectedLevel[0].roleName;
        this.getRoleHierarchies(this.roleName);
    }

    onEntityChange(entity: EntityHierarchy) {
        if (entity) {
            if (!this.selectedIdentifier) {
                this.selectedIdentifier = [];
            }
            this.action = NoDataActionType.filter;
            this.selectedIdentifier[0] = entity;
            this.updateQueryParams();
            this.getScorecardData();
        }
    }
    clearIdFromQueryParams() {
        this.idFromQueryParams = '';
    }

    getScorecardData() {
        this.getRoleKPIs();
        this.setScorecardDetails();
        this.setFundedProduction();
        this.setProductPurposeMixCard();
        this.pageTitle = this.roleName ? this.roleName + ' Scorecard' : 'Branch Scorecard';
    }

    initializeDetailCard() {
        this.detailsCard = this.performanceScorecardService.initializeScoreDetailsCard(
            ScorecardType.location,
            this.action
        );
    }

    setScorecardDetails() {
        this.initializeDetailCard();
        let scorecardParameters = this.getParameters();
        this.performanceScorecardService.getScoreDetails(scorecardParameters).subscribe(
            (res) => {
                if (res && res != null) {
                    this.detailsCard = this.performanceScorecardService.updateScoreDetails(
                        this.detailsCard,
                        res
                    );
                } else {
                    this.detailsCard.hasData = false;
                    this.detailsCard.isDataLoading = false;
                    this.detailsCard = Object.assign({}, this.detailsCard);
                }
            },
            (error) => {
                this.detailsCard.action = NoDataActionType.error;
                this.detailsCard.hasData = false;
                this.detailsCard.isDataLoading = false;
                this.detailsCard = Object.assign({}, this.detailsCard);
                throw new Error(error);
            }
        );
    }

    getIdentifier() {
        if (this.idFromQueryParams) {
            return this.idFromQueryParams;
        } else if (this.selectedIdentifier) {
            return this.selectedIdentifier[0].hierarchyIdentifier;
        }
    }
    getRoleName() {
        if (this.roleName) {
            return this.roleName;
        }
        return this.selectedLevel[0].roleName;
    }

    getParameters() {
        let scorecardParameters: IScorecardParameters = {
            scoreCardType: ScorecardType.location,
            entityIdentifier: this.idFromQueryParams
                ? this.idFromQueryParams
                : this.selectedIdentifier[0].hierarchyIdentifier,
            entityType: this.roleName,
            fromDatePeriod: this.fromDatePeriod,
            ymc: this.ymc,
            chartGroup: '',
        };
        return Object.assign({}, scorecardParameters);
    }

    setFundedProduction() {
        let chartTitle = 'Funded Production';
        this.largeCardInteriorOptions = this.performanceScorecardService.initializeProductionChart(
            this.pageId,
            this.action
        );
        this.performanceScorecardService.getProductionData(this.getParameters()).subscribe(
            (res: ScorecardProductionWebModel[]) => {
                res.map((data) => {
                    // TODO: fix this in the data sproc
                    if (data.milestone && data.milestone.toLowerCase() === 'funded') {
                        data.productionChartType = 0;
                    } else {
                        data.productionChartType = 1;
                    }
                });
                this.largeCardInteriorOptions = this.performanceScorecardService.drawFundedProductionChart(
                    res,
                    this.largeCardInteriorOptions,
                    chartTitle
                );
            },
            (error) => {
                this.largeCardInteriorOptions.hasData = true;
                this.largeCardInteriorOptions.isDataReady = true;
                this.largeCardInteriorOptions.action = NoDataActionType.error;
                this.largeCardInteriorOptions = this.performanceScorecardService.drawFundedProductionChart(
                    undefined,
                    this.largeCardInteriorOptions,
                    chartTitle
                );
                throw new Error(error);
            }
        );
    }

    getRoleKPIs() {
        let kpiDataHandler = new ScorecardKpicardDataHandler(
            this.reusableFunctions,
            this.sanitizer
        );
        let params: IScorecardKpiParams = {
            cardId: 'performanceEntityScoreCardKpi',
            title: 'Key Performance Indicators',
            data: [],
            action: this.action,
            searchTerm: '', // TODO; Set this values
            cardDrilldownUrl: '', // TODO; Set this values
            styleSet: { 'cardlist-container-area': { height: '440px' } },
        };
        this.kpiCardData = kpiDataHandler.initializeCards(params);

        this.performanceScorecardService
            .getScorecardKPIs(
                this.roleName,
                this.idFromQueryParams
                    ? this.idFromQueryParams
                    : this.selectedIdentifier[0].hierarchyIdentifier,
                this.fromDatePeriod,
                this.ymc
            )
            .subscribe(
                (res) => {
                    this.kpiCardData = kpiDataHandler.handleKPILargeCards(
                        this.kpiCardData,
                        res,
                        this.fromDatePeriod
                    );
                },
                (error) => {
                    params.action = NoDataActionType.error;
                    params.isDataReady = true;
                    this.kpiCardData = kpiDataHandler.initializeCards(params);
                    throw new Error(error);
                }
            );
    }

    setDisplayOptionsSelected() {
        if (this.displayOptions.find((item) => item.optionKey === 'dateRangeRolling')) {
            this.fromDatePeriod = this.displayOptions.find(
                (item) => item.optionKey === 'dateRangeRolling'
            ).selectedOptions;
        }

        if (this.displayOptions.find((item) => item.optionKey === 'scorecardAsOf')) {
            this.ymc = this.displayOptions.find(
                (item) => item.optionKey === 'scorecardAsOf'
            ).selectedOptions;
        }
    }

    setProductPurposeMixCard() {
        this.productionPurposeMixPieChartData = this.performanceScorecardService.initializeDonutcharts();
        this.productionPurposeMixPieChartData.noDataType.action = this.action;
        let scorecardParameters = this.getParameters();
        let chartGroups = ['Product', 'Purpose'];

        chartGroups.forEach((chartGroup) => {
            scorecardParameters.chartGroup = chartGroup.toLowerCase();
            this.performanceScorecardService.getPositionMix(scorecardParameters).subscribe(
                (res) => {
                    this.productionPurposeMixPieChartData = this.performanceScorecardService.productPurposeMixCardLoad(
                        res,
                        this.productionPurposeMixPieChartData,
                        chartGroup
                    );
                },
                (error) => {
                    this.productionPurposeMixPieChartData['isLoading' + chartGroup + 'Mix'] = false;
                    this.productionPurposeMixPieChartData.noDataType.action =
                        NoDataActionType.error;
                    this.productionPurposeMixPieChartData = this.performanceScorecardService.productPurposeMixCardLoad(
                        null,
                        this.productionPurposeMixPieChartData,
                        chartGroup
                    );
                }
            );
        });
    }

    onItemDeSelectRole(event) {
        this.selectedLevel = [event];
    }

    onItemDeSelectEntity(event) {
        this.selectedIdentifier = [event];
    }
}
